import { useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { MessageSquareIcon, SendIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { desksEndpoints } from '@/api/endpoints';
import { integrationIconByType } from '@/components/atoms/Icons/integrationIconByType';
import { MODAL_NEW_MESSAGE } from '@/components/organisms/Modals/ModalConductor';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import { Skeleton } from '@/components/ui/skeleton';
import useDebounce from '@/hooks/useDebounce';
import { type Desks } from '@/hooks/useDesks';
import { pushModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { timeFromNow } from '@/util/dateUtils';
import { normalizePhone } from '@/util/validation/patterns';

import { CommandLinkItem } from '../CommandLinkItem';
import { MIN_QUERY_LENGTH, useConversationSearch } from '../hooks/useConversationSearch';

type SearchConversationDrillDownProps = {
  search: string;
  onClose: () => void;
  onNavigate: (path: string, label: string) => void;
};

const DEBOUNCE_MS = 300;

const formatPhone = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  const parsed = parsePhoneNumberFromString(value.startsWith('+') ? value : `+${value}`);
  return parsed?.formatInternational() ?? null;
};

export const SearchConversationDrillDown = ({
  search,
  onClose,
  onNavigate,
}: SearchConversationDrillDownProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const debouncedSearch = useDebounce(search, DEBOUNCE_MS);
  const { results, isSearching } = useConversationSearch(debouncedSearch);

  const desksData = queryClient.getQueryData<Desks>([desksEndpoints.desks]);
  const serviceDeskIds = useMemo(
    () => new Set(desksData?.desks?.filter((d) => d.is_service_desk).map((d) => d.desk_id)),
    [desksData]
  );

  const trimmed = search.trim();
  const isDebouncePending = trimmed !== debouncedSearch.trim();
  const loading = isSearching || isDebouncePending;

  // Hint state: empty or too short
  if (trimmed.length < MIN_QUERY_LENGTH) {
    return (
      <CommandGroup forceMount>
        <p className="px-2 py-6 text-center text-sm text-muted-foreground">
          {t('command_palette.conversation_search_hint')}
        </p>
      </CommandGroup>
    );
  }

  // Loading skeletons
  if (loading) {
    return (
      <CommandGroup forceMount>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="flex items-center gap-3 px-2 py-1.5">
            <Skeleton className="size-4 rounded" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </CommandGroup>
    );
  }

  const handleSendNewMessage = () => {
    trackEvent(EventName.CommandPaletteSelect, {
      type: 'action',
      item: 'send_new_message',
      query_length: search.length,
    });
    const normalized = normalizePhone(trimmed);
    dispatch(pushModal(MODAL_NEW_MESSAGE, normalized ? { initialTo: normalized } : {}));
    onClose();
  };

  // No results
  if (results.length === 0) {
    return (
      <CommandGroup forceMount>
        <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          {t('command_palette.no_conversation_found', { 0: trimmed })}
        </p>
        <CommandItem value="send-new-message" onSelect={handleSendNewMessage} forceMount>
          <SendIcon className="size-3" />
          <span>{t('command_palette.send_new_message_to', { 0: trimmed })}</span>
        </CommandItem>
      </CommandGroup>
    );
  }

  // Results list
  return (
    <CommandGroup heading={t('command_palette.search_conversations')} forceMount>
      {results.map((result) => {
        const phone = formatPhone(result.channelUserId);
        const label = result.userName || phone || result.userEmail || result.sessionId;
        // Show phone/email as subtitle when label is the name
        const subtitle = result.userName ? phone || result.userEmail : null;
        const date = timeFromNow(result.startTime);
        const ChannelIcon =
          integrationIconByType[result.channel as keyof typeof integrationIconByType] ??
          MessageSquareIcon;
        const relativePath = serviceDeskIds.has(result.deskId)
          ? `/chats/${result.deskId}/conversations/${result.sessionId}?category=all`
          : `/analytics/account-chat-logs?session_id=${result.sessionId}`;
        const path = slug ? `/${slug}${relativePath}` : '#';

        return (
          <CommandLinkItem
            key={result.sessionId}
            value={`conversation-${result.sessionId}`}
            href={path}
            onSelect={() => onNavigate(relativePath, label)}
            forceMount
          >
            <ChannelIcon className="size-3" />
            <span className="flex-1 truncate">
              {label}
              {subtitle && <span className="ml-1.5 text-xs text-muted-foreground">{subtitle}</span>}
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {result.totalUserMessages === 0
                ? t('command_palette.no_messages')
                : t(
                    result.totalUserMessages === 1
                      ? 'command_palette.message_count_singular'
                      : 'command_palette.message_count',
                    { 0: result.totalUserMessages }
                  )}{' '}
              · {date}
            </span>
          </CommandLinkItem>
        );
      })}
    </CommandGroup>
  );
};
